import React from 'react'
import {
  IonRow,
  IonCol,
  IonLabel,
  IonText,
  IonToolbar,
  IonItem,
  IonToggle,
  IonItemDivider,
  IonList,
} from '@ionic/react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getSpecialReportsSelector } from '../selectors'
import TextArea from '../../../components/textArea/TextArea.component'
import DateTimePicker from '../../../components/calendar/DateTimePicker/DateTimePicker.component'
import SInput from '../../../components/stringInput/StringInput.component'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import {
  setSubject,
  setDateOccurred,
  setSupervisor,
  setPersonnelIssue,
  setCrew,
  setIsUrgent,
  setIsFlagReport,
  saveSpecialReport,
} from '../store/specialReports.actions'
import { IOption } from '../../../common/common.types'
import { getCrewListDataSelector, getCrewListSelector } from '../../crew/selectors'
import { IOptionsWithSeq } from '../store/specialReports.types'

const Details: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const specialReportsState = useSelector(getSpecialReportsSelector)
  const { subject, dateOccurred, author, supervisor, personnelIssue, crew, base, unit, isUrgent, isFlagReport } =
    specialReportsState
  const crewListData = useSelector(getCrewListDataSelector)
  const crewList = useSelector(getCrewListSelector)

  const totalCrewCount = crewList.length >= 4 ? crewList.length : 4

  const handleSubjectChange = (e: CustomEvent) => {
    dispatch(setSubject(e.detail.value || ''))
    dispatch(saveSpecialReport())
  }

  const handleDateOccurredChange = (selectedDate: string | null) => {
    dispatch(setDateOccurred(selectedDate))
    dispatch(saveSpecialReport())
  }

  const handleSupervisorChange = (option: IOption | null) => {
    dispatch(setSupervisor(option))
    dispatch(saveSpecialReport())
  }

  const handlePersonnelIssueChange = (option: IOption | null) => {
    dispatch(setPersonnelIssue(option))
    dispatch(saveSpecialReport())
  }

  const handleCrewChange = (option: IOption | null, seq: number) => {
    const updatedCrew = crew.filter((c: IOptionsWithSeq) => c.seq !== seq)
    if (option && option.id) {
      updatedCrew.push({ id: option.id, name: option.name || '', seq })
    }
    updatedCrew.sort((a, b) => a.seq - b.seq)
    dispatch(setCrew(updatedCrew))
    dispatch(saveSpecialReport())
  }

  const getCrewBySeq = (seq: number) => crew.find((c: IOptionsWithSeq) => c.seq === seq) || null

  const handleIsUrgentClick = () => {
    dispatch(setIsUrgent(!isUrgent))
    dispatch(saveSpecialReport())
  }

  const handleIsFlagReportClick = () => {
    dispatch(setIsFlagReport(!isFlagReport))
    dispatch(saveSpecialReport())
  }

  return (
    <>
      <IonRow className='mt-1 mb-1'>
        <IonCol size='12'>
          <IonLabel>
            <IonText className='header-title'>{t('specialReports.pcrDetails')}</IonText>
          </IonLabel>
        </IonCol>
        <IonCol size='12' className='ml-1 ion-no-padding'>
          <IonRow>
            <IonCol size='2'>
              <IonLabel>{t('specialReports.base')}:</IonLabel>
            </IonCol>
            <IonCol size='10'>
              <IonLabel color='medium'>{base?.name || ''}</IonLabel>
            </IonCol>
          </IonRow>
        </IonCol>
        <IonCol size='12' className='ml-1 ion-no-padding'>
          <IonRow>
            <IonCol size='2'>
              <IonLabel>{t('specialReports.unit')}:</IonLabel>
            </IonCol>
            <IonCol size='10'>
              <IonLabel color='medium'>{unit?.name || ''}</IonLabel>
            </IonCol>
          </IonRow>
        </IonCol>
      </IonRow>
      <IonToolbar color='light' className='ion-no-padding p-10'>
        <TextArea
          rows={3}
          cols={20}
          maxlength={500}
          dataTestID='subject'
          label={t('specialReports.subject')}
          value={subject || ''}
          onChangeAction={handleSubjectChange}
        />
      </IonToolbar>
      <DateTimePicker
        dataTestID='dateOccurred'
        label={t('specialReports.dateTimeOccurred')}
        selectedDateValue={dateOccurred || null}
        action={handleDateOccurredChange}
        disabled={false}
      />

      <IonItemDivider color='light' className='ion-no-padding-top ion-no-padding-bottom pt-5 pb-5'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('specialReports.details.markAs')}
        </IonLabel>
      </IonItemDivider>
      <IonList>
        <IonRow>
          <IonCol className='picklist'>
            <IonItem lines='none' className='toggle-item'>
              <IonLabel>{t('specialReports.details.urgent')}</IonLabel>
              <IonToggle
                slot='start'
                name='isUrgent'
                checked={isUrgent}
                onClick={handleIsUrgentClick}
                color='zoll-blue-secondary'
              />
            </IonItem>
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <IonItem lines='none' className='toggle-item'>
              <IonLabel>{t('specialReports.details.flagReport')}</IonLabel>
              <IonToggle
                slot='start'
                name='isFlagReport'
                checked={isFlagReport}
                onClick={handleIsFlagReportClick}
                color='zoll-blue-secondary'
              />
            </IonItem>
          </IonCol>
        </IonRow>
      </IonList>
      <hr className='field-divider' />
      <SInput
        dataTestID='author'
        label={t('specialReports.yourName')}
        inputValue={author?.name || ''}
        disabled={true}
        onChangeAction={() => {
          return
        }}
      />
      <hr className='field-divider' />
      <SingleSelect
        dataTestID='supervisor'
        label={t('specialReports.supervisor')}
        listHeader={t('specialReports.supervisor')}
        options={crewListData}
        selectedOption={supervisor}
        action={handleSupervisorChange}
      />
      <hr className='field-divider' />
      <SingleSelect
        dataTestID='personnelIssue'
        label={t('specialReports.personnelIssue')}
        listHeader={t('specialReports.personnelIssue')}
        options={crewListData}
        selectedOption={personnelIssue}
        action={handlePersonnelIssueChange}
      />
      <IonItemDivider color='light' />
      {Array.from({ length: totalCrewCount }, (_, seq) => {
        const crewMember = getCrewBySeq(seq)
        return (
          <React.Fragment key={seq}>
            <SingleSelect
              dataTestID={`crew-${seq + 1}`}
              label={`${t('specialReports.crew')} ${seq + 1}`}
              listHeader={`${t('specialReports.crew')} ${seq + 1}`}
              options={crewListData}
              selectedOption={crewMember}
              action={(option: IOption | null) => handleCrewChange(option, seq)}
            />
            {seq < totalCrewCount - 1 && <hr className='field-divider' />}
          </React.Fragment>
        )
      })}
    </>
  )
}

export default Details
